import * as React from "react";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import {CartonGradeCatalogDTO} from "@/api/data-contracts";
import {getI18n, useTranslation} from "react-i18next";
import {CommonI18N, RequestI18N} from "@/translations";
import {booleanField} from "@/common/fields/boolean-field";
import {formatNumber} from "@/lib/utils";
import {EDGE_CRUSH_UNIT, GSM} from "@/constants";
import {OrderDataType} from "@/model/orderData";

/**
 * Component to display the technical parameters of a carton grade in a table format.
 */
export const GradeParametersTable = ({
    grade,
    originalGrade,
    mode
}: {
    grade?: CartonGradeCatalogDTO;
    originalGrade?: CartonGradeCatalogDTO;
    mode?: OrderDataType;
}) => {
    const { t } = useTranslation();
    const locale = getI18n().language;
    const showBothColumns = (mode === "quote" || mode === "accept") && originalGrade && grade;

    if (!grade?.values) {
        return null;
    }

    const values = grade.values;
    const originalValues = originalGrade?.values;

    // Determine which translation keys to use based on the mode
    const getColumnHeaders = () => {
        if (mode === "quote") {
            return {
                firstColumn: RequestI18N.grade.table.requesterGrade,
                secondColumn: RequestI18N.grade.table.yourGrade
            };
        } else if (mode === "accept") {
            return {
                firstColumn: RequestI18N.grade.table.yourGrade,
                secondColumn: RequestI18N.grade.table.supplierGradeAccept
            };
        } else {
            // Default or "request" mode
            return {
                singleColumn: RequestI18N.grade.table.yourGrade
            };
        }
    };

    const columnHeaders = getColumnHeaders();

    return (
        <Table
            size="small"
            sx={{
                minWidth: "300px",
                "& td, & th": {
                    padding: "4px 8px",
                    fontSize: "0.75rem",
                    color: "text.primary",
                    backgroundColor: "background.paper",
                    borderColor: "divider"
                }
            }}
        >
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }} />
                    {showBothColumns ? (
                        <>
                            <TableCell sx={{ fontWeight: "bold" }}>
                                {t(
                                    columnHeaders.firstColumn
                                        ? columnHeaders.firstColumn
                                            : RequestI18N.grade.table.requesterGrade
                                )}
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                                {t(
                                    columnHeaders.secondColumn
                                        ? columnHeaders.secondColumn
                                            : RequestI18N.grade.table.supplierGrade
                                )}
                            </TableCell>
                        </>
                    ) : (
                        <TableCell sx={{ fontWeight: "bold" }}>
                            {t(
                                columnHeaders.singleColumn
                                    ? columnHeaders.singleColumn
                                        : RequestI18N.grade.table.yourGrade
                            )}
                        </TableCell>
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.flute)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.flute || "-"}</TableCell>}
                    <TableCell>{values.flute || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.adOuterLiner)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.outerLiner || "-"}</TableCell>}
                    <TableCell>{values.outerLiner || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.w1Flute)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.fluteOne || "-"}</TableCell>}
                    <TableCell>{values.fluteOne || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.zd1Liner)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.linerOne || "-"}</TableCell>}
                    <TableCell>{values.linerOne || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.w2Flute)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.fluteTwo || "-"}</TableCell>}
                    <TableCell>{values.fluteTwo || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.zd2Liner)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.linerTwo || "-"}</TableCell>}
                    <TableCell>{values.linerTwo || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.w3Flute)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.fluteThree || "-"}</TableCell>}
                    <TableCell>{values.fluteThree || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.idInnerLiner)}
                    </TableCell>
                    {showBothColumns && <TableCell>{originalValues?.innerLiner || "-"}</TableCell>}
                    <TableCell>{values.innerLiner || "-"}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.weightGM2)}
                    </TableCell>
                    {showBothColumns && (
                        <TableCell>{`${formatNumber(originalValues?.weight, 2, locale) ?? "-"} ${GSM}`}</TableCell>
                    )}
                    <TableCell>{`${formatNumber(values.weight, 2, locale) ?? "-"} ${GSM}`}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.ectEdgeCrush)}
                    </TableCell>
                    {showBothColumns && (
                        <TableCell>{`${formatNumber(originalValues?.edgeCrushTest, 2, locale) ?? "-"} ${EDGE_CRUSH_UNIT}`}</TableCell>
                    )}
                    <TableCell>{`${formatNumber(values.edgeCrushTest, 2, locale) ?? "-"} ${EDGE_CRUSH_UNIT}`}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell component="th" sx={{ fontWeight: "bold" }}>
                        {t(RequestI18N.grade.table.nf)}
                    </TableCell>
                    {showBothColumns && (
                        <TableCell>
                            {
                                booleanField(
                                    originalValues?.wetStrength,
                                        t(CommonI18N.message.yes),
                                        t(CommonI18N.message.no),
                                        t(RequestI18N.grade.table.nf),
                                    "-"
                                ).value
                            }
                        </TableCell>
                    )}
                    <TableCell>
                        {
                            booleanField(
                                values.wetStrength,
                                    t(CommonI18N.message.yes),
                                    t(CommonI18N.message.no),
                                    t(RequestI18N.grade.table.nf),
                                "-"
                            ).value
                        }
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
};
